import * as z from 'zod/v4'

import {importActions, importDescriptions} from './namespaces/import'
import {learnActions, learnDescriptions} from './namespaces/learn'
import {noteActions, noteDescriptions, noteModelParamOverrides} from './namespaces/note'
import {recordingActions, recordingDescriptions} from './namespaces/recording'

const commandBranches = [...noteActions, ...recordingActions, ...importActions, ...learnActions] as const

export const CommandSchema = z.discriminatedUnion('command', [...commandBranches])

export type Command = z.infer<typeof CommandSchema>
export type CommandValue = Command['command']

// One human-readable description per command, used to register commands as LLM tools.
// `satisfies Record<CommandValue, string>` fails the build if a command is missing or extra,
// so this stays in sync with the schema.
const commandDescriptions = {
  ...noteDescriptions,
  ...recordingDescriptions,
  ...importDescriptions,
  ...learnDescriptions
} satisfies Record<CommandValue, string>

// Structurally matches the backend's LlmToolDefinition
// (services/common-backend/src/llm-clients/llm_message_client.ts). common-universal cannot
// import from common-backend (wrong dependency direction), so the shape is declared here and
// the server consumes it directly. inputSchema is the JSON Schema of the command's params
// (empty object when it takes none) — the tool name carries the command identity, so the
// command discriminant and caller are never in the schema the LLM fills.
export interface CommandToolDefinition {
  name: CommandValue
  description: string
  inputSchema: Record<string, unknown>
}

// Lets a command give the LLM more params than it gives the client. note_read is the only entry
// today: the model needs the full filter set (pattern, contextLines, etc.) to call it usefully,
// but the client only ever needs noteId (see Backend/plans/chatbot-note-read-tool.md). Without an
// entry here, commandToolDefinitions below falls back to the command's own branch params, so both
// audiences keep seeing the same schema — that's still true for every command but note_read.
const modelParamOverrides: Partial<Record<CommandValue, z.ZodType>> = {
  ...noteModelParamOverrides
}

export const commandToolDefinitions: CommandToolDefinition[] = commandBranches.map((branch) => {
  const name = branch.shape.command.value
  // The tool's args ARE the command's params: the tool name already identifies the command,
  // so the command discriminant and caller never belong in the schema the model fills. Build
  // inputSchema from the params sub-schema only (unwrapping an optional wrapper); a command
  // with no params exposes an empty object, so the model calls it with {}.
  const {shape} = branch
  const branchParams = 'params' in shape ? shape.params : undefined
  const unwrappedBranchParams = branchParams instanceof z.ZodOptional ? branchParams.unwrap() : branchParams
  const inputSchema = z.toJSONSchema(modelParamOverrides[name] ?? unwrappedBranchParams ?? z.strictObject({}))
  return {name, description: commandDescriptions[name], inputSchema}
})

const branchByCommand: Map<string, (typeof commandBranches)[number]> = new Map(
  commandBranches.map((branch) => [branch.shape.command.value, branch])
)

// Trims a tool call's args down to its command's own branch params — the same client contract
// CommandSchema parses the client's copy against — before they reach the client. A command whose
// modelParamOverrides schema is broader than its branch (note_read today) has the difference
// dropped here; every other command's branch params already are what the client needs, so this
// is a no-op for them. The server keeps the model's original, unrestricted args in its own
// history for later use (e.g. note_read's server-side filter step).
export function projectClientArgs(tool: string, args: Record<string, unknown>): Record<string, unknown> {
  const branch = branchByCommand.get(tool)
  const params = branch && 'params' in branch.shape ? branch.shape.params : undefined
  const unwrapped = params instanceof z.ZodOptional ? params.unwrap() : params
  if (!(unwrapped instanceof z.ZodObject)) return args
  const allowedKeys = Object.keys(unwrapped.shape)
  return Object.fromEntries(Object.entries(args).filter(([key]) => allowedKeys.includes(key)))
}

export * from './caller'
export * from './response'
export * from './defineAction'
